import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import * as Sentry from '@sentry/react';
import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

interface RouteErrorFallbackProps {
  onReload: () => void;
  onRetry: () => void;
}

function RouteErrorFallback({ onReload, onRetry }: RouteErrorFallbackProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '60vh',
        textAlign: 'center',
        px: 2,
      }}
    >
      <Typography variant="h6" gutterBottom>
        Une erreur inattendue est survenue.
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
        La page n'a pas pu s'afficher correctement.
      </Typography>
      <Stack direction="row" spacing={2}>
        <Button variant="contained" onClick={onReload}>
          Recharger la page
        </Button>
        <Button variant="outlined" onClick={onRetry}>
          Réessayer
        </Button>
      </Stack>
    </Box>
  );
}

/**
 * Error boundary scoped to the routed content, sitting *inside* the router (so
 * it can reset on navigation) and just above the route <Suspense>. It catches
 * render errors and failed lazy imports that the global `vite:preloadError`
 * handler didn't already recover from, and shows an actionable fallback with a
 * reload button, unlike the static root Sentry boundary in App.tsx, which
 * stays in place as the ultimate net around the providers.
 *
 * `key={location.pathname}` remounts the boundary on every navigation, so a
 * user who navigates away (via a still-alive layout) escapes the error state.
 */
export default function RouteErrorBoundary({ children }: { children: ReactNode }) {
  const location = useLocation();
  return (
    <Sentry.ErrorBoundary
      key={location.pathname}
      fallback={({ resetError }) => (
        <RouteErrorFallback onReload={() => window.location.reload()} onRetry={resetError} />
      )}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}
